import isEmpty from 'lodash/isEmpty';

const fieldErrorClassName = 'ui basic error large pointing prompt label';
const fieldErrorListClassName = 'ui basic error pointing prompt label list';

const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const normalizeFieldErrors = error => {
    if (Array.isArray(error)) {
        return error.flatMap(normalizeFieldErrors).filter(message => !isEmpty(message));
    }

    if (error && typeof error === 'object') {
        if (hasOwnProperty(error, 'message')) {
            return normalizeFieldErrors(error.message);
        }

        const structuredFieldError = hasOwnProperty(error, 'field') || hasOwnProperty(error, 'value');
        return Object.entries(error)
            .filter(([key]) => !structuredFieldError || (key !== 'field' && key !== 'value'))
            .flatMap(([, value]) => normalizeFieldErrors(value))
            .filter(message => !isEmpty(message));
    }

    return isEmpty(error) ? [] : [String(error)];
};

const getDirectFieldError = error => {
    if (typeof error === 'string' || Array.isArray(error)) {
        return error;
    }

    return error && typeof error === 'object' && hasOwnProperty(error, 'message') ? error : null;
};

const setBooleanAttribute = (element, attribute, value) => {
    if (value) {
        element[attribute] = true;
        element.setAttribute(attribute, '');
    }
};

const disableFormControls = root => {
    ['input', 'textarea', 'select', 'button'].forEach(selector => {
        root.querySelectorAll(selector).forEach(element => {
            element.disabled = true;
            element.setAttribute('disabled', '');
            if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
                element.readOnly = true;
                element.setAttribute('readonly', '');
            }
        });
    });
};

const appendFieldError = (container, error, {className = ''} = {}) => {
    const messages = normalizeFieldErrors(error);
    if (isEmpty(messages)) {
        return false;
    }

    if (container.className?.split(/\s+/).includes('field') &&
        !container.className.split(/\s+/).includes('error')) {
        container.className += ' error';
    }

    const errorField = document.createElement('div');
    if (messages.length > 1) {
        errorField.className = `${fieldErrorListClassName}${className ? ` ${className}` : ''}`;
        const errorList = document.createElement('ul');
        errorList.className = 'list';
        messages.forEach(message => {
            const errorItem = document.createElement('li');
            errorItem.textContent = message;
            errorList.appendChild(errorItem);
        });
        errorField.appendChild(errorList);
    } else {
        errorField.className = `${fieldErrorClassName}${className ? ` ${className}` : ''}`;
        errorField.textContent = messages[0];
    }
    container.appendChild(errorField);
    return true;
};

const createFields = (id, fieldElements = []) => {
    const fields = document.createElement('div');
    fields.className = 'fields';
    fields.id = id;
    fieldElements.forEach(fieldElement => fields.appendChild(fieldElement));
    return fields;
};

const createField = ({label, id, className = '', fieldElement, help, error} = {}) => {
    const fieldContainer = document.createElement('div');
    fieldContainer.className = (className ? className + ' ' : '') + 'field';

    if (id) {
        fieldContainer.id = id;
    }

    if (label) {
        if (label instanceof HTMLDivElement) {
            fieldContainer.className += ' right labeled';
            fieldContainer.appendChild(label);
        } else {
            const labelElement = document.createElement('label');
            labelElement.setAttribute('for', fieldElement.id);
            labelElement.textContent = label;
            fieldContainer.appendChild(labelElement);
        }
    }

    fieldContainer.appendChild(fieldElement);
    appendFieldError(fieldContainer, error);

    if (help) {
        fieldContainer.insertAdjacentHTML('beforeend', `<div class="ui top pointing basic large label">${help}</div>`);
    }

    return fieldContainer;
};

const createInputField = ({
                              label,
                              id,
                              name,
                              className = '',
                              type,
                              step,
                              min,
                              value = '',
                              placeHolder,
                              readOnly,
                              disabled,
                              help,
                              error
                          } = {}) => {
    const inputElement = document.createElement('input');
    if (id) {
        inputElement.id = id;
    }
    inputElement.name = name;
    inputElement.type = type;
    inputElement.value = value;

    if (step) {
        inputElement.step = step;
    }

    if (placeHolder) {
        inputElement.placeholder = placeHolder;
    }

    setBooleanAttribute(inputElement, 'readOnly', readOnly);
    setBooleanAttribute(inputElement, 'disabled', disabled);

    if (typeof min !== 'undefined') {
        inputElement.min = min;
    }

    return createField({label, className, fieldElement: inputElement, help, error});
};

const createTextareaField = ({
                                 label,
                                 id,
                                 name,
                                 rows,
                                 className = '',
                                 value = '',
                                 placeHolder,
                                 error
                             } = {}) => {
    const textAreaElement = document.createElement('textarea');
    if (id) {
        textAreaElement.id = id;
    }
    if (rows) {
        textAreaElement.rows = rows;
    }
    textAreaElement.name = name;
    textAreaElement.textContent = value;

    if (placeHolder) {
        textAreaElement.placeholder = placeHolder;
    }

    return createField({label, className, fieldElement: textAreaElement, error});
};

const createDropdown = ({
                            label,
                            help,
                            id = '',
                            name = '',
                            className = '',
                            value = '',
                            error,
                            multiple = false,
                            clearable = false,
                            searchable = false,
                            disabled = false,
                            allowAdditions = false,
                            options = []
                        } = {}) => {
    const selectElement = document.createElement('select');
    selectElement.className = 'ui fluid selection dropdown';
    if (id) {
        selectElement.id = id;
        selectElement.className = `${selectElement.className} ${id}`;
    }
    selectElement.name = name;

    if (searchable) {
        selectElement.className = selectElement.className + ' search';
    }

    if (clearable) {
        selectElement.className = selectElement.className + ' clearable';
    }

    if (multiple) {
        selectElement.className = selectElement.className + ' multiple';
    }

    if (disabled) {
        selectElement.disabled = true;
        selectElement.setAttribute('disabled', '');
    }

    if (allowAdditions) {
        selectElement.className = selectElement.className + ' addition';
        selectElement.dataset.allowAdditions = 'true';
    }

    options.forEach(option => {
        const optionElement = document.createElement('option');
        optionElement.value = option.value;
        optionElement.textContent = option.name;
        if (option.value === value) {
            optionElement.selected = true;
            optionElement.setAttribute('selected', '');
        }
        selectElement.appendChild(optionElement);
    });

    return createField({label, help, className, fieldElement: selectElement, error});
};

const createCheckboxes = ({label, name, value, type, options, error, className} = {}) => {
    const fields = document.createElement('div');
    fields.className = (className ? className + ' ' : '') + 'grouped fields';

    if (label) {
        const labelElement = document.createElement('label');
        labelElement.setAttribute('for', name);
        labelElement.appendChild(document.createTextNode(label));
        fields.appendChild(labelElement);
    }

    options.forEach(option => {
        const id = `${name}${option.value}`;
        const checkboxContainer = document.createElement('div');
        checkboxContainer.className = `ui ${type} checkbox`;

        const inputElement = document.createElement('input');
        inputElement.id = id;
        inputElement.className = 'hidden';
        inputElement.name = name;
        inputElement.type = type;
        inputElement.value = option.value;

        if (value === option.value) {
            checkboxContainer.className += ' checked';
            inputElement.checked = true;
            inputElement.className = 'checked';
            inputElement.setAttribute('checked', '');
        }

        if (option.disabled) {
            checkboxContainer.className += ' disabled';
            inputElement.disabled = true;
            inputElement.className = 'disabled';
            inputElement.setAttribute('disabled', '');
        }

        setBooleanAttribute(inputElement, 'readOnly', option.readOnly);

        if (option.onChange) {
            inputElement.addEventListener('change', option.onChange);
        }

        const labelElement = document.createElement('label');
        labelElement.appendChild(document.createTextNode(option.label));
        labelElement.setAttribute('for', id);

        checkboxContainer.appendChild(inputElement);
        checkboxContainer.appendChild(labelElement);

        const fieldContainer = document.createElement('div');
        fieldContainer.className = (option.className ? option.className + ' ' : '') + 'field';
        fieldContainer.appendChild(checkboxContainer);

        if (option.help) {
            fieldContainer.insertAdjacentHTML('beforeend', `<div class="ui left pointing basic large label">${option.help}</div>`);
        }

        fields.appendChild(fieldContainer);
    });

    appendFieldError(fields, error);

    return fields;
};

const createTable = (labels = [], columns = []) => {
    const headerRow = document.createElement('tr');
    labels.forEach(label => {
        const header = document.createElement('th');
        header.appendChild(document.createTextNode(label));
        headerRow.appendChild(header);
    });

    const headers = document.createElement('thead');
    headers.appendChild(headerRow);

    const dataColumns = document.createElement('tr');
    columns.forEach(column => {
        const data = document.createElement('td');
        data.appendChild(column);
        dataColumns.appendChild(data);
    });

    const body = document.createElement('tbody');
    body.appendChild(dataColumns);

    const tableElement = document.createElement('table');
    tableElement.className = 'ui very basic table';
    tableElement.appendChild(headers);
    tableElement.appendChild(body);
    return tableElement;
};

export {
    appendFieldError,
    createCheckboxes,
    createDropdown,
    createField,
    createFields,
    createInputField,
    createTable,
    createTextareaField,
    disableFormControls,
    getDirectFieldError,
    normalizeFieldErrors
};
